/** biome-ignore-all lint/suspicious/noConsole: telemetry layer needs console diagnostics */
import { Environment } from '@cobliteam/react-environments';
import { runAfterPageSettles } from '@cobliteam/utils/idle';
import type { PostHog as PostHogClient, PostHogConfig, Survey } from 'posthog-js';

let client: PostHogClient | null = null;
let initPromise: Promise<PostHogClient | null> | null = null;

const env = Environment.getInstance().getEnvironment();

// disable_surveys (true) kills the surveys feature entirely, including
// getActiveMatchingSurveys. Use disable_surveys_automatic_display to suppress
// only the native popover renderer while keeping the API available.
const DEFAULT_CONFIG: Partial<PostHogConfig> = {
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  disable_surveys_automatic_display: true,
  persistence: 'localStorage+cookie',
  person_profiles: 'identified_only',
};

async function loadClient(): Promise<PostHogClient | null> {
  if (!env.posthogToken) {
    console.info('[PostHog] token missing — surveys disabled.');
    return null;
  }
  try {
    const mod = await import('posthog-js');
    const posthog = mod.default;
    posthog.init(env.posthogToken, {
      ...DEFAULT_CONFIG,
      api_host: env.posthogHost,
    });
    return posthog;
  } catch (error) {
    console.error('[PostHog] failed to initialize', error);
    return null;
  }
}

// PostHog ops are no-ops until enable() is called. This keeps non-targeted
// roles from ever loading the SDK or burning event/flag quota.
export const PostHog = {
  enable(): Promise<PostHogClient | null> {
    if (initPromise) return initPromise;
    initPromise = new Promise<void>((resolve) => runAfterPageSettles(resolve)).then(loadClient).then((c) => {
      client = c;
      return c;
    });
    return initPromise;
  },

  isEnabled(): boolean {
    return initPromise !== null;
  },

  getClient(): PostHogClient | null {
    return client;
  },

  identify(distinctId: string, properties?: Record<string, unknown>) {
    if (!initPromise) return;
    void initPromise.then((c) => c?.identify(distinctId, properties));
  },

  capture(event: string, properties?: Record<string, unknown>) {
    if (!initPromise) return;
    void initPromise.then((c) => c?.capture(event, properties));
  },

  reset() {
    if (!initPromise) return;
    void initPromise.then((c) => c?.reset());
  },

  async getActiveSurveys(forceReload = false): Promise<Survey[]> {
    if (!initPromise) return [];
    const c = await initPromise;
    if (!c) return [];
    return new Promise((resolve) => {
      c.getActiveMatchingSurveys((surveys) => resolve(surveys ?? []), forceReload);
    });
  },

  // Bypasses PostHog targeting/dedup filters. Use only for internal testers
  // (e.g. cobler role) who need to repeatedly preview a live survey.
  async getAllSurveys(forceReload = false): Promise<Survey[]> {
    if (!initPromise) return [];
    const c = await initPromise;
    if (!c) return [];
    return new Promise((resolve) => {
      c.getSurveys((surveys) => resolve(surveys ?? []), forceReload);
    });
  },
};

export type { PostHogClient, Survey };
